import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["ariaLabel", "isDisabled", "isRequired", "isInvalid", "isChecked", "label", "mode", "name", "onChange", "value", "testId", "analyticsContext"];
/** @jsx jsx */
import { forwardRef, memo, useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next/usePlatformLeafEventHandler';
import __noop from '@atlaskit/ds-lib/noop';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { DN600, N80, N900 } from '@atlaskit/theme/colors';
import GlobalTheme from '@atlaskit/theme/components';
import { fontFamily as getFontFamily } from '@atlaskit/theme/constants';
import getRadioCustomProperties from './styles';
var packageName = "@atlaskit/radio";
var packageVersion = "5.6.10";
var fontFamily = getFontFamily();
var noop = __noop;
var labelPaddingStyles = css({
  padding: "var(--ds-space-025, 2px)".concat(" ", "var(--ds-space-050, 4px)")
});
var labelStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  position: 'relative',
  alignItems: 'flex-start',
  fontFamily: fontFamily,
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '&[data-disabled]': {
    color: "var(--ds-text-disabled, ".concat(N80, ")"),
    cursor: 'not-allowed'
  }
});
var lightLabelStyles = css({
  color: "var(--ds-text, ".concat(N900, ")")
});
var darkLabelStyles = css({
  color: "var(--ds-text, ".concat(DN600, ")")
});
var radioStyles = css({
  display: 'flex',
  // TODO https://product-fabric.atlassian.net/browse/DSP-10507 revisit and remove the scale of 14/24
  /*
    The circle should be 14px * 14px centred in a 24px * 24px box.
    This is inclusive of a 2px border and inner circle with 2px radius.
    There is a Chrome bug that makes the circle become an oval and the
    inner circle not be centred at various zoom levels. This bug is fixed
    in all browsers if a scale of 14/24 is applied.
  */
  width: '24px',
  height: '24px',
  margin: "var(--ds-space-0, 0px)",
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: 'var(--radio-background-color)',
  /* Border should multiply by 24/14 to offset scale, a scale of 12 / 7 is to fix a Chrome bug that makes the circle become an oval and the
    inner circle not be centred at various zoom levels */
  border: "".concat(getBooleanFF('platform.design-system-team.update-border-radio-checkbox_7askv') ? "var(--ds-border-width, 1px)" : 'calc(2px * 12 / 7)', " solid var(--radio-border-color)"),
  borderRadius: "var(--ds-border-radius-circle, 50%)",
  MozAppearance: 'none',
  outline: 'none',
  /*
    Change the variables --radio-background-color, --radio-border-color,
    -radio-dot-color and -radio-dot-opacity according to user interactions.
    All other variables are constant
  */
  '--radio-background-color': 'var(--local-background)',
  '--radio-border-color': 'var(--local-border)',
  '--radio-dot-color': 'var(--local-dot-checked)',
  '--radio-dot-opacity': 0,
  /* 24px * 7 / 12 === 14px height and width */
  transform: 'scale(calc(7 / 12))',
  transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
  verticalAlign: 'top',
  WebkitAppearance: 'none',
  '&::after': {
    /* Height and width should by 4px, multiply by 24/14 to offset scale */
    width: 'calc(4px * 12 / 7)',
    height: 'calc(4px * 12 / 7)',
    position: 'absolute',
    background: 'var(--radio-dot-color)',
    // TODO Delete this comment after verifying spacing token -> previous value `'50%'`
    borderRadius: "var(--ds-border-radius-circle, 50%)",
    content: "''",
    opacity: 'var(--radio-dot-opacity)',
    transition: 'background-color 0.2s ease-in-out, opacity 0.2s ease-in-out'
  },
  '&:hover': {
    '--radio-background-color': 'var(--local-background-hover)',
    '--radio-border-color': 'var(local-border-hover)'
  },
  '&:active': {
    '--radio-background-color': 'var(--local-background-active)'
  },
  '&:focus': {
    '--radio-border-color': 'var(--local-border-focus)'
  },
  '&:checked': {
    '--radio-background-color': 'var(--local-background-checked)',
    '--radio-border-color': 'var(--local-background-checked)',
    '--radio-dot-opacity': 1
  },
  '&:checked:hover': {
    '--radio-background-color': 'var(--local-background-checked-hover)',
    '--radio-border-color': 'var(--local-background-checked-hover)'
  },
  '&:checked:active': {
    '--radio-background-color': 'var(--local-background-checked-active)',
    '--radio-border-color': 'var(--local-border-hover)',
    '--radio-dot-color': 'var(--local-dot-active)'
  },
  '&:checked:focus': {
    '--radio-border-color': 'var(--local-border-focus)'
  },
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '&[data-invalid], &:checked[data-invalid]': {
    '--radio-border-color': 'var(--local-invalid)'
  },
  '&:disabled, &:disabled:hover, &:disabled:focus, &:disabled:active, &:disabled[data-invalid]': {
    cursor: 'not-allowed',
    '--radio-background-color': 'var(--local-background-disabled)',
    '--radio-border-color': 'var(--local-border-disabled)',
    '--radio-dot-color': 'var(--local-dot-disabled)'
  }
});
var RadioWithMode = /*#__PURE__*/forwardRef(function Radio(props, ref) {
  var ariaLabel = props.ariaLabel,
    _props$isDisabled = props.isDisabled,
    isDisabled = _props$isDisabled === void 0 ? false : _props$isDisabled,
    isRequired = props.isRequired,
    _props$isInvalid = props.isInvalid,
    isInvalid = _props$isInvalid === void 0 ? false : _props$isInvalid,
    _props$isChecked = props.isChecked,
    isChecked = _props$isChecked === void 0 ? false : _props$isChecked,
    label = props.label,
    mode = props.mode,
    name = props.name,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? noop : _props$onChange,
    value = props.value,
    testId = props.testId,
    analyticsContext = props.analyticsContext,
    rest = _objectWithoutProperties(props, _excluded);
  var onChangeAnalytics = usePlatformLeafEventHandler({
    fn: onChange,
    action: 'changed',
    analyticsData: analyticsContext,
    componentName: 'radio',
    packageName: packageName,
    packageVersion: packageVersion
  });
  var radioCustomProperties = useMemo(function () {
    return css(getRadioCustomProperties(mode));
  }, [mode]);
  return jsx("label", {
    "data-testid": testId && "".concat(testId, "--radio-label"),
    "data-disabled": isDisabled ? 'true' : undefined,
    css: [labelStyles, mode === 'light' ? lightLabelStyles : darkLabelStyles]
  }, jsx("input", _extends({}, rest, {
    "aria-label": ariaLabel,
    checked: isChecked,
    disabled: isDisabled,
    name: name,
    onChange: onChangeAnalytics,
    required: isRequired,
    type: "radio",
    value: value,
    "data-testid": testId && "".concat(testId, "--radio-input")
    // isInvalid is used in a nonstandard way so cannot
    // use :invalid selector
    ,
    "data-invalid": isInvalid ? 'true' : undefined
    // TODO radioCustomProperties can be defined at top of file when legacy theming removed
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    ,
    css: [radioStyles, radioCustomProperties],
    ref: ref
  })), label ? jsx("span", {
    css: labelPaddingStyles
  }, label) : null);
});

/**
 * __Radio__
 *
 * A radio input allows users to select only one option from a number of choices. Radio is generally displayed in a radio group.
 *
 * - [Examples](https://atlassian.design/components/radio/examples)
 * - [Code](https://atlassian.design/components/radio/code)
 * - [Usage](https://atlassian.design/components/radio/usage)
 */
var Radio = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function Radio(props, ref) {
  return jsx(GlobalTheme.Consumer, null, function (_ref) {
    var mode = _ref.mode;
    return jsx(RadioWithMode, _extends({}, props, {
      ref: ref,
      mode: mode
    }));
  });
}));
export default Radio;